import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  Select,
  Spin,
  Space,
  Typography,
  Dropdown,
} from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import { useEffect } from 'react';

import {
  Avatar,
  ConfirmDeletingElement,
  FastCreationModal,
  Phone,
} from '@app/components';
import { CustomAlert } from '@app/components/CustomAlert';
import { SelectField, SnilsInput } from '@app/components/FormFields';
import { DictionarySelect } from '@app/components/RecordModal/components/MedicalCard/components';
import { MedicalDictionaryType } from '@app/components/RecordModal/components/MedicalCard/data';
import { signerRoleOptions } from '@app/components/RecordModal/components/MedicalCard/data/dictionaries';
import { SetBranchesComponent } from '@app/components/SetBranchesComponent';
import {
  ADD_POSITION_LABEL,
  BRANCHES_LABEL,
  DATE_PICKER_FORMAT,
  EMAIL_LENGTH_VALIDATION,
  EMAIL_TYPE_VALIDATION,
  ERROR,
  ERROR_REQUIRED_FIELD,
  GENDER,
  ISO_FORMAT_INDEX,
  OFF,
  ONE_TIME_CODE,
  PASSWORD_VALIDATION_RULES,
  PREFIX,
  rangePickerLocale,
  UNKNOWN_GENDER_ID,
  WARNING,
} from '@app/constants';
import { useUsersAccessRights } from '@app/hooks';
import { selectActiveBranchCountryId, useAppSelector } from '@app/store';
import type { NEmployees, NSmallGlobalEntities } from '@app/types';

import { useEmployeeInfoTab } from '@pages/Employees/EditEmployee/components/EmployeeInfoTab/hooks/useEmployeeInfoTab';
import { useRegisterEditEmployeeUnsavedChanges } from '@pages/Employees/EditEmployee/hooks/useEditEmployeeUnsavedChanges';

import '@pages/Employees/EditEmployee/components/EmployeeInfoTab/styles/EmployeeInfoTab.desktop.scss';
import '@pages/Employees/EditEmployee/components/EmployeeInfoTab/styles/EmployeeInfoTab.tablet.scss';

export const EmployeeInfoTab = (data: NEmployees.EmployeeInfoTabProps) => {
  const {
    employee,
    newData,
    setNewData,
    setEmployee,
    setIsNeedToUpdateUserPreview,
    setHandleUnsavedChanges,
  } = data;

  const {
    infoTabError,
    contextHolder,
    infoTabForm,
    sendDataToDb,
    positions,
    isModalOpen,
    modalForm,
    addPosition,
    handleOk,
    handleCancel,
    finishModal,
    isInfoLoader,
    setInfoTabError,
    open,
    allBranches,
    selectedBranches,
    setSelectedBranches,
    showBranchesModal,
    hideBranchesModal,
    setPhoneCountryId,
    confirmDeletingModalOpen,
    setConfirmDeletingModalOpen,
    deleteEmployee,
    isFirstLoad,
    setIsFirstLoad,
    isUpdated,
    setIsUpdated,
    setPhoneValidation,
    handleUnsavedChanges,
    fallbackName,
    photoUrl,
    avatarDropdownItems,
    IsAdmin,
  } = useEmployeeInfoTab(employee, setEmployee, setIsNeedToUpdateUserPreview);

  const {
    hasUserRightToCreatePosition,
    hasUserRightToChangeBranches,
    hasUserRightToDelete,
  } = useUsersAccessRights();
  const activeBranchCountry = useAppSelector(selectActiveBranchCountryId);
  useEffect(() => {
    if (isFirstLoad && employee?.nameFirst && allBranches.length) {
      setIsFirstLoad(false);
      const birthday = employee?.dateBirth
        ? employee?.dateBirth.slice(0, 10)
        : undefined;

      setSelectedBranches(
        allBranches.filter(({ Id: globalId }) =>
          employee?.userBranchIds.find((id) => id === globalId)
        )
      );

      infoTabForm.setFieldsValue({
        NameLast: employee?.nameLast,
        NameFirst: employee?.nameFirst,
        NameMiddle: employee?.nameMiddle,
        Email: employee?.email,
        Password: employee?.password,
        Position: employee?.positionId === null ? 0 : employee?.positionId,
        Gender: employee?.gender as NSmallGlobalEntities.GenderForForm,
        Description: employee?.description,
        Note: employee?.note,
        DateBirth: birthday ? dayjs(birthday) : '',
        IsDeleted: employee?.isDeleted,
        Phone: {
          Phone: employee?.phone,
          CountryId: employee?.phoneCountryId || activeBranchCountry || 1,
        },
        Snils: employee?.snils,
        SignerRole: employee?.signerRole,
        PositionCode: employee?.positionCode,
        SpecialityCodeNsi: employee?.specialityCodeNsi,
        TelegramId: employee?.telegramId,
      });

      setPhoneCountryId(employee?.phoneCountryId);
    }
  }, [
    employee,
    allBranches,
    isFirstLoad,
    setIsFirstLoad,
    setSelectedBranches,
    infoTabForm,
    setPhoneCountryId,
    activeBranchCountry,
  ]);

  useRegisterEditEmployeeUnsavedChanges({
    handleUnsavedChanges,
    setHandleUnsavedChanges,
  });

  const isFormSpinning = isInfoLoader || isFirstLoad;

  return (
    <div className={`${PREFIX}employee-info-tab` + ' bc-form'}>
      {contextHolder}
      <Spin spinning={isFormSpinning}>
        <Form
          onValuesChange={() => setIsUpdated(true)}
          form={infoTabForm}
          onFinish={sendDataToDb}
          style={{ maxWidth: 800, width: '100%' }}
          disabled={isFormSpinning}
          autoComplete={OFF}
          initialValues={{
            NameLast: employee?.nameLast ?? null,
            NameFirst: employee?.nameFirst ?? null,
            NameMiddle: employee?.nameMiddle ?? null,
            Email: employee?.email ?? null,
            Position:
              employee?.positionId === null ? 0 : employee?.positionId || null,
            Gender: employee?.gender ?? null,
            Description: employee?.description ?? null,
            Note: employee?.note ?? null,
            DateBirth: employee?.dateBirth
              ? dayjs(employee?.dateBirth.slice(0, 10))
              : '',
            IsDeleted: employee?.isDeleted ?? null,
            Phone: employee?.phone ?? null,
            PhoneCode: employee?.phoneCode ?? null,
            PhoneCountryId: employee?.phoneCountryId ?? null,
            Snils: employee?.snils ?? null,
            SignerRole: employee?.signerRole ?? null,
            PositionCode: employee?.positionCode ?? null,
            SpecialityCodeNsi: employee?.specialityCodeNsi ?? null,
            TelegramId: employee?.telegramId ?? null,
          }}
        >
          <Space direction="vertical" size="large" className="form-space">
            {IsAdmin && (
              <div className={'container'}>
                <Typography.Text className="label" strong data-testid="surname">
                  Фото
                </Typography.Text>
                <div className="employee_avatar ant-form-item">
                  <Avatar src={photoUrl} nameFirst={fallbackName} />
                  <Dropdown menu={{ items: avatarDropdownItems }}>
                    <Button
                      data-testid="change-avatar"
                      type="link"
                      disabled={isFormSpinning}
                    >
                      Изменить
                    </Button>
                  </Dropdown>
                </div>
              </div>
            )}
            <div className="container">
              <Typography.Text className="label" strong data-testid="surname">
                Фамилия
              </Typography.Text>

              <Form.Item name="NameLast" className="mg-0">
                <Input
                  data-testid="surname-input"
                  autoComplete={ONE_TIME_CODE}
                  size="large"
                  maxLength={60}
                  onChange={(e) => {
                    const newValue = {
                      ...newData.infoData,
                      nameLast: e.target.value,
                    };
                    setNewData({
                      ...newData,
                      infoData: newValue,
                    });
                  }}
                />
              </Form.Item>
            </div>
            <div className="container">
              <Typography.Text
                className="label-require label"
                strong
                data-testid="name"
              >
                Имя
              </Typography.Text>

              <Form.Item
                name="NameFirst"
                rules={[
                  {
                    required: true,
                    message: ERROR_REQUIRED_FIELD,
                  },
                ]}
                className="mg-0"
              >
                <Input
                  data-testid="name-input"
                  autoComplete={ONE_TIME_CODE}
                  size="large"
                  maxLength={60}
                  onChange={(e) => {
                    const newValue = {
                      ...newData.infoData,
                      nameFirst: e.target.value,
                    };
                    setNewData({
                      ...newData,
                      infoData: newValue,
                    });
                  }}
                />
              </Form.Item>
            </div>
            <div className="container">
              <Typography.Text
                className="label"
                strong
                data-testid="patronymic"
              >
                Отчество
              </Typography.Text>

              <Form.Item name="NameMiddle" className="mg-0">
                <Input
                  data-testid="patronymic-input"
                  autoComplete={ONE_TIME_CODE}
                  size="large"
                  maxLength={60}
                  onChange={(e) => {
                    const newValue = {
                      ...newData.infoData,
                      nameMiddle: e.target.value,
                    };
                    setNewData({
                      ...newData,
                      infoData: newValue,
                    });
                  }}
                />
              </Form.Item>
            </div>
            <div className="container">
              <Typography.Text
                strong
                className="label label-require"
                data-testid="email"
              >
                Email
              </Typography.Text>

              <Form.Item
                name="Email"
                rules={[
                  { required: true, message: ERROR_REQUIRED_FIELD },
                  EMAIL_TYPE_VALIDATION,
                  EMAIL_LENGTH_VALIDATION,
                ]}
                className="mg-0 second-column"
              >
                <Input
                  data-testid="email-input"
                  maxLength={100}
                  autoComplete={ONE_TIME_CODE}
                  size="large"
                  onChange={(e) => {
                    const newValue = {
                      ...newData.infoData,
                      email: e.target.value,
                    };
                    setNewData({
                      ...newData,
                      infoData: newValue,
                    });
                  }}
                />
              </Form.Item>
            </div>
            <div className="container">
              <Typography.Text
                strong
                className="label"
                data-testid="new-password"
              >
                Пароль
              </Typography.Text>

              <Form.Item
                name="Password"
                rules={[...PASSWORD_VALIDATION_RULES]}
                className="mg-0 second-column"
              >
                <Input.Password
                  data-testid="new-password-input"
                  autoComplete={ONE_TIME_CODE}
                  minLength={6}
                  maxLength={60}
                  size="large"
                  onChange={(e) => {
                    const newValue = {
                      ...newData.infoData,
                      password: e.target.value,
                    };
                    setNewData({
                      ...newData,
                      infoData: newValue,
                    });
                  }}
                />
              </Form.Item>
            </div>
            <div className="container">
              <Typography.Text strong className="label" data-testid="phone">
                Мобильный телефон
              </Typography.Text>
              <Form.Item name="Phone" className="mg-0 second-column">
                <Phone
                  name="Phone"
                  required
                  disabled={isInfoLoader}
                  onValidate={setPhoneValidation}
                  phoneInputDataTestId="phone-input"
                />
              </Form.Item>
            </div>
            <div className="container">
              <Typography.Text className="label" strong data-testid="telegram">
                Telegram
              </Typography.Text>
              <Form.Item name="TelegramId" className="mg-0 second-column">
                <Input
                  data-testid="telegram-input"
                  autoComplete={ONE_TIME_CODE}
                  size="large"
                  maxLength={100}
                  onChange={(e) => {
                    const newValue = {
                      ...newData.infoData,
                      telegramId: e.target.value,
                    };
                    setNewData({
                      ...newData,
                      infoData: newValue,
                    });
                  }}
                />
              </Form.Item>
            </div>

            <SnilsInput fieldName="Snils" label="СНИЛС врача (11 цифр)" />

            <SelectField
              label="Медицинская должность"
              fieldName={'SignerRole'}
              placeholder="Выберите должность"
              options={signerRoleOptions}
              size="large"
            />

            <DictionarySelect
              type={MedicalDictionaryType.Positions}
              title="Код должности врача по НСИ"
              name={'PositionCode'}
            />

            <DictionarySelect
              type={MedicalDictionaryType.Specialities}
              title="Код специальности из НСИ"
              name={'SpecialityCodeNsi'}
            />

            <div className="container">
              <Typography.Text className="label" strong data-testid="position">
                Должность
              </Typography.Text>

              <Form.Item name="Position" className="mg-0 second-column">
                <Select
                  data-testid="position-select"
                  size="large"
                  showSearch
                  optionFilterProp="label"
                  options={positions.map(({ Name, Id }) => {
                    return {
                      value: Id,
                      label: Name,
                    };
                  })}
                  onChange={(value) => {
                    const newValue = {
                      ...newData.infoData,
                      positionId: value === 0 ? null : value,
                    };
                    setNewData({
                      ...newData,
                      infoData: newValue,
                    });
                  }}
                  popupRender={(menu) => (
                    <>
                      {hasUserRightToCreatePosition ? (
                        <>
                          <Button
                            type="link"
                            size="small"
                            onClick={addPosition}
                          >
                            {ADD_POSITION_LABEL}
                          </Button>
                          <Divider style={{ margin: '8px 0' }} />
                          {menu}
                        </>
                      ) : (
                        <>{menu}</>
                      )}
                    </>
                  )}
                />
              </Form.Item>
            </div>
            <div className="container">
              <Typography.Text className="label" strong data-testid="gender">
                Пол
              </Typography.Text>

              <Form.Item name="Gender" className="mg-0">
                <Select
                  data-testid="gender-select"
                  size="large"
                  options={GENDER}
                  onChange={(value) => {
                    const newValue = {
                      ...newData.infoData,
                      gender: value === UNKNOWN_GENDER_ID ? null : value,
                    };
                    setNewData({
                      ...newData,
                      infoData: newValue,
                    });
                  }}
                />
              </Form.Item>
            </div>
            <div className="container">
              <Typography.Text
                className="label"
                strong
                data-testid="birth-date"
              >
                Дата рождения
              </Typography.Text>

              <Form.Item name="DateBirth" className="mg-0">
                <DatePicker
                  data-testid="birth-date-select"
                  autoComplete={ONE_TIME_CODE}
                  locale={rangePickerLocale}
                  format={DATE_PICKER_FORMAT}
                  size="large"
                  onChange={(value) => {
                    const newValue = {
                      ...newData.infoData,
                      dateBirth: value
                        ? value.format(DATE_PICKER_FORMAT[ISO_FORMAT_INDEX])
                        : null,
                    };

                    setNewData({
                      ...newData,
                      infoData: newValue,
                    });
                  }}
                  disabledDate={(current) =>
                    current.unix() >= moment().startOf('day').unix()
                  }
                />
              </Form.Item>
            </div>
            <div className="container description-container">
              <Typography.Text
                className="label"
                strong
                data-testid="description"
              >
                Описание
              </Typography.Text>

              <Form.Item name="Description" className="mg-0">
                <Input.TextArea
                  data-testid="description-input"
                  autoComplete={ONE_TIME_CODE}
                  rows={4}
                  maxLength={260}
                  onChange={(e) => {
                    const newValue = {
                      ...newData.infoData,
                      description: e.target.value,
                    };
                    setNewData({
                      ...newData,
                      infoData: newValue,
                    });
                  }}
                />
              </Form.Item>
            </div>
            <div className="container description-container">
              <Typography.Text
                className="label"
                strong
                data-testid="description"
              >
                Примечание
              </Typography.Text>

              <Form.Item name="Note" className="mg-0">
                <Input.TextArea
                  data-testid="description-input"
                  autoComplete={ONE_TIME_CODE}
                  rows={4}
                  maxLength={260}
                  onChange={(e) => {
                    const newValue = {
                      ...newData.infoData,
                      description: e.target.value,
                    };
                    setNewData({
                      ...newData,
                      infoData: newValue,
                    });
                  }}
                />
              </Form.Item>
            </div>
            <Divider className="divider" />
            <div
              className="branches-container"
              style={{
                display: hasUserRightToChangeBranches ? 'block' : 'none',
              }}
            >
              <Typography.Title
                level={3}
                className="mg-0"
                data-testid="branches"
              >
                {BRANCHES_LABEL}
              </Typography.Title>
              <Typography.Text type="secondary" data-testid="branches-hint">
                Укажите, в каких филиалах может работать исполнитель
              </Typography.Text>
              <SetBranchesComponent
                open={open}
                onCancel={hideBranchesModal}
                selectedBranches={selectedBranches}
                setSelectedBranches={setSelectedBranches}
                allBranches={allBranches}
                showUserModal={showBranchesModal}
                tooltipLabel="Укажите, в каких филиалах может работать исполнитель"
                infoData={newData}
                setInfoData={setNewData}
                dataKey={'infoData'}
                user={employee}
              />
            </div>
            <Form.Item style={{ marginTop: 10 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                {infoTabError && (
                  <CustomAlert
                    errorType={ERROR}
                    serverError={infoTabError}
                    setServerError={setInfoTabError}
                  />
                )}

                {isUpdated && (
                  <CustomAlert
                    errorType={WARNING}
                    serverError={'Есть несохранённые изменения'}
                  />
                )}

                <div style={{ display: 'flex', gap: '16px' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="save-button"
                    size="large"
                    loading={isInfoLoader}
                    data-testid="save-edit-employee"
                  >
                    Сохранить
                  </Button>

                  <Button
                    danger
                    className="save-button"
                    disabled={isInfoLoader}
                    size="large"
                    onClick={() => setConfirmDeletingModalOpen(true)}
                    style={{ display: hasUserRightToDelete ? 'block' : 'none' }}
                    data-testid="delete-employee"
                  >
                    Удалить
                  </Button>
                </div>
              </Space>
            </Form.Item>
          </Space>
        </Form>
      </Spin>
      <FastCreationModal
        title="Создание должности"
        isModalOpen={isModalOpen}
        handleOk={handleOk}
        handleCancel={handleCancel}
        modalForm={modalForm}
        finishModal={finishModal}
        nameMaxLength={40}
        descriptionMaxLength={260}
      />
      <ConfirmDeletingElement
        modalOpen={confirmDeletingModalOpen}
        setModalOpen={setConfirmDeletingModalOpen}
        handleDelete={deleteEmployee}
        content="Вы уверены, что хотите удалить исполнителя?"
        title="Удаление исполнителя"
        modalDataTestId="delete-employee-popup"
        titleDataTestId="delete-title"
        descriptionDataTestId="delete-description"
      />
    </div>
  );
};
